import { createStyles } from 'antd-style'

export const useStyles = createStyles(({ token }) => ({
  hero: {
    borderRadius: token.borderRadiusLG,
    '.ant-card-body': {
      padding: '18px 20px'
    }
  },

  heroIdentityLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    // Hugs the tile and the name instead of taking the row. It was `flex: 1`,
    // which stretched the link — and with it the hover state and the click
    // target — across the empty space between the name and the buttons on the
    // far side. `0 1 auto` keeps the shrink, so a long name still ellipsises
    // rather than pushing them off the card.
    flex: '0 1 auto',
    minWidth: 0,
    color: token.colorText,
    textDecoration: 'none',
    borderRadius: token.borderRadius,
    padding: 2,
    margin: -2,
    transition: `background-color ${token.motionDurationMid}, transform ${token.motionDurationMid}`,

    '&:hover h3.ant-typography': {
      color: token.colorPrimaryText
    },

    '&:focus-visible': {
      outline: `2px solid ${token.colorPrimary}`,
      outlineOffset: 2
    }
  },

  heroAvatar: {
    width: 44,
    height: 44,
    borderRadius: 10,
    background: `linear-gradient(135deg, ${token.colorPrimary} 0%, ${token.colorPrimaryHover} 100%)`,
    color: token.colorTextLightSolid,
    fontSize: 18,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
    boxShadow: '0 4px 12px rgba(0, 127, 110, 0.18)',
    overflow: 'hidden'
  },

  /**
   * `contain`, not `cover`: a workspace logo is a mark with its own margins, and
   * cropping it to fill the tile cut the mark's own edges off. The settings page
   * has always shown it whole — these two now agree.
   */
  heroAvatarImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain' as const,
    display: 'block'
  },

  /** A logo brings its own ground; the brand gradient behind it only muddies it. */
  heroAvatarWithImage: {
    background: token.colorBgContainer,
    boxShadow: 'none'
  },

  heroGreeting: {
    fontSize: 12,
    lineHeight: 1.3,
    color: token.colorTextSecondary,
    margin: 0
  },

  heroTitle: {
    margin: '0 !important' as unknown as string,
    fontWeight: 600,
    fontSize: 22,
    lineHeight: 1.2
  },

  heroMetaLabel: {
    fontSize: 11,
    color: token.colorTextSecondary,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontWeight: 500
  },

  heroMetaValue: {
    fontSize: 12,
    fontWeight: 500,
    fontVariantNumeric: 'tabular-nums'
  },

  heroDivider: {
    height: 1,
    background: token.colorBorderSecondary,
    margin: '14px 0 12px'
  },

  kpiStrip: {
    '.ant-card-body': {
      padding: 0
    }
  },

  kpiTile: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    padding: '14px 16px',
    flex: 1,
    minWidth: 0,
    cursor: 'pointer',
    color: token.colorText,
    transition: `background-color ${token.motionDurationMid}`,
    textDecoration: 'none',

    '&:hover': {
      backgroundColor: token.colorFillTertiary
    },

    '&:focus-visible': {
      outline: 'none',
      backgroundColor: token.colorFillTertiary
    }
  },

  kpiTileIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: token.borderRadiusSM,
    background: token.colorPrimaryBg,
    color: token.colorPrimaryText
  },

  kpiTileLabel: {
    fontSize: 11,
    color: token.colorTextSecondary,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.04em'
  },

  kpiTileValue: {
    margin: 0,
    fontSize: 24,
    fontWeight: 600,
    lineHeight: 1.05,
    fontVariantNumeric: 'tabular-nums'
  },

  kpiDivider: {
    width: 1,
    background: token.colorBorderSecondary,
    alignSelf: 'stretch'
  },

  panelCard: {
    '.ant-card-body': {
      padding: 0
    },
    height: '100%'
  },

  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '12px 14px',
    borderBottom: `1px solid ${token.colorBorderSecondary}`
  },

  panelTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: token.colorText
  },

  panelSubtitle: {
    fontSize: 12,
    color: token.colorTextSecondary
  },

  activityRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '10px 14px',

    '& + &': {
      borderTop: `1px solid ${token.colorBorderSecondary}`
    }
  },

  activitySeverityDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    marginTop: 6,
    flex: '0 0 auto'
  },

  activityActor: {
    fontSize: 13,
    fontWeight: 500
  },

  activityAction: {
    fontFamily: token.fontFamilyCode,
    fontSize: 11,
    padding: '1px 6px',
    borderRadius: token.borderRadiusSM,
    background: token.colorFillSecondary,
    color: token.colorTextSecondary,
    whiteSpace: 'nowrap'
  },

  activityMeta: {
    fontSize: 11,
    color: token.colorTextSecondary
  },

  activityEmpty: {
    padding: '20px 14px 22px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6
  },

  activityEmptyIcon: {
    width: 44,
    height: 44,
    borderRadius: token.borderRadiusLG,
    background: token.colorPrimaryBg,
    color: token.colorPrimaryText,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4
  },

  signalRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '10px 14px',

    '& + &': {
      borderTop: `1px solid ${token.colorBorderSecondary}`
    }
  },

  signalIcon: {
    width: 26,
    height: 26,
    borderRadius: 6,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto'
  },

  chartKpiLabel: {
    fontSize: 11,
    color: token.colorTextSecondary,
    textTransform: 'uppercase',
    letterSpacing: '0.04em'
  },

  chartKpiValue: {
    margin: `0 !important`,
    fontSize: 24,
    fontWeight: 600,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums'
  },

  chartAxis: {
    fontSize: 10,
    color: token.colorTextSecondary,
    fontVariantNumeric: 'tabular-nums'
  },

  chartColumn: {
    borderRadius: 3,
    transition: `background-color ${token.motionDurationFast}`,

    '&:hover': {
      backgroundColor: token.colorFillTertiary
    }
  },

  appHealthRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '12px 14px',

    '& + &': {
      borderTop: `1px solid ${token.colorBorderSecondary}`
    }
  },

  appIcon: {
    width: 32,
    height: 32,
    borderRadius: 7,
    color: token.colorTextLightSolid,
    fontWeight: 700,
    fontSize: 13,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto'
  },

  metricLabel: {
    fontSize: 10,
    color: token.colorTextSecondary,
    textTransform: 'uppercase',
    letterSpacing: '0.04em'
  },

  metricValue: {
    fontSize: 13,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums'
  }
}))
